import React, { useState, useEffect } from 'react';

const QuestionRenderer = ({ question, initialAnswer, onAnswer, onBack }) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedOptions, setSelectedOptions] = useState([]); // For Checkbox
    const [selectedOption, setSelectedOption] = useState(null); // For Radio

    const [validationError, setValidationError] = useState(null); // Inline error

    // Reset or Pre-fill state when question or initialAnswer changes
    useEffect(() => {
        // Default Reset
        setInputValue('');
        setSelectedOptions([]);
        setSelectedOption(null);
        setValidationError(null);

        // Pre-fill if answer exists
        if (initialAnswer) {
            if (initialAnswer.optionId) {
                setSelectedOption(initialAnswer.optionId);
            }
            if (initialAnswer.optionIds) {
                setSelectedOptions(initialAnswer.optionIds);
            }
            // For Number/Text, value is in numericValue or valueString
            if (initialAnswer.valueString) {
                setInputValue(initialAnswer.valueString);
            } else if (initialAnswer.numericValue !== null && initialAnswer.numericValue !== undefined) {
                setInputValue(String(initialAnswer.numericValue));
            }
        }
    }, [question.id, initialAnswer]);

    const { type, options, title, description } = question; // Removed isRequired

    // Handle Number Input Change
    const handleNumberChange = (e) => {
        const val = e.target.value;

        // Clear previous error on typing
        setValidationError(null);

        // Regex to check if input contains any non-digit
        if (/[^0-9]/.test(val)) {
            setValidationError("숫자만 입력할 수 있습니다.");
            return;
        }

        setInputValue(val);
    };

    const handleSubmit = () => {
        // Validation
        // Always enforce validation for this widget as per requirement
        if (type === 'CHECKBOX') {
            if (selectedOptions.length === 0) {
                if (options.length === 1) {
                    setValidationError('항목이 하나인 경우, 반드시 체크해야 다음 단계로 진행할 수 있습니다.');
                } else {
                    setValidationError('하나 이상 선택해주세요.');
                }
                return;
            }
        }
        if ((type === 'RADIO' || type === 'SELECT') && !selectedOption) {
            setValidationError('옵션을 선택해주세요.');
            return;
        }
        if ((type === 'NUMBER' || type === 'TEXT') && !inputValue) {
            setValidationError('값을 입력해주세요.');
            return;
        }

        if (type === 'NUMBER') {
            const numVal = parseInt(inputValue, 10);
            if (isNaN(numVal) || numVal < 1) {
                setValidationError('1 이상의 자연수만 입력할 수 있습니다.');
                return;
            }
        }

        // Build Payload
        let answerData = {
            questionId: question.id
        };

        if (type === 'RADIO' || type === 'SELECT') {
            answerData.optionId = selectedOption;
        } else if (type === 'CHECKBOX') {
            answerData.optionIds = selectedOptions;
        } else if (type === 'NUMBER' || type === 'TEXT') {
            answerData.value = inputValue;
            // For NUMBER, send the first option's ID as the context for pricing
            if (type === 'NUMBER' && options.length > 0) {
                answerData.optionId = options[0].id;
            }
        }

        onAnswer(answerData); // Parent will handle API call
    };

    const handleCheckboxChange = (optId) => {
        setSelectedOptions(prev => {
            if (prev.includes(optId)) return prev.filter(id => id !== optId);
            return [...prev, optId];
        });
    };

    return (
        <div className="question-container">
            <h3 className="question-title">
                {title}
                {/* Removed isRequired check */}
            </h3>
            {description && <p className="question-desc">{description}</p>}

            {/* Helper Text based on Type */}
            <div style={{ marginBottom: '15px', fontSize: '0.9rem', color: '#007bff', fontWeight: 500 }}>
                {type === 'CHECKBOX' && (
                    options.length === 1
                        ? '※ 다음 단계로 진행하려면 반드시 체크해주세요.'
                        : '※ 하나 이상 선택해주세요.'
                )}
                {(type === 'RADIO' || type === 'SELECT') && '※ 항목 중 하나를 선택해주세요.'}
                {(type === 'NUMBER' || type === 'TEXT') && '※ 내용을 입력해주세요.'}
            </div>

            <div className="input-area">
                {type === 'RADIO' && options.map(opt => (
                    <div key={opt.id} className="option-item radio-item">
                        <label>
                            <input
                                type="radio"
                                name={`q-${question.id}`}
                                value={opt.id}
                                checked={selectedOption === opt.id}
                                onChange={() => {
                                    setSelectedOption(opt.id);
                                    setValidationError(null);
                                }}
                            />
                            <span className="label-text">{opt.label}</span>
                        </label>
                    </div>
                ))}

                {type === 'SELECT' && (
                    <div className="select-wrapper">
                        <select
                            className="form-control"
                            style={{ width: '100%', padding: '10px' }}
                            value={selectedOption || ''}
                            onChange={(e) => {
                                setSelectedOption(Number(e.target.value));
                                setValidationError(null);
                            }}
                        >
                            <option value="" disabled>선택해주세요</option>
                            {options.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {type === 'CHECKBOX' && options.map(opt => (
                    <div key={opt.id} className="option-item checkbox-item">
                        <label>
                            <input
                                type="checkbox"
                                value={opt.id}
                                checked={selectedOptions.includes(opt.id)}
                                onChange={() => {
                                    handleCheckboxChange(opt.id);
                                    setValidationError(null);
                                }}
                            />
                            <span className="label-text">{opt.label}</span>
                        </label>
                    </div>
                ))}

                {(type === 'NUMBER' || type === 'TEXT') && (
                    <div className="text-input-wrapper">
                        {/* Show label for NUMBER/TEXT type if option exists */}
                        {options.length > 0 && (
                            <span className="option-label" style={{ fontWeight: 'bold', display: 'block' }}>{options[0].label}</span>
                        )}
                        <input
                            type={type === 'NUMBER' ? 'text' : 'text'} // Force text to block 'e'
                            inputMode={type === 'NUMBER' ? 'numeric' : 'text'}
                            pattern={type === 'NUMBER' ? "[0-9]*" : undefined}
                            className="form-control"
                            value={inputValue}
                            onChange={type === 'NUMBER' ? handleNumberChange : (e) => {
                                setInputValue(e.target.value);
                                setValidationError(null);
                            }}
                            placeholder={type === 'NUMBER' ? "수량을 입력하세요 (자연수)" : "입력해주세요"}
                        />
                        {/* Display unit if exists in uiMetaJson? */}
                    </div>
                )}
            </div>

            {/* Error Message Above Buttons */}
            {validationError && (
                <div style={{
                    color: '#d32f2f',
                    fontSize: '0.95rem',
                    marginBottom: '15px',
                    fontWeight: 'bold',
                    padding: '10px',
                    background: '#ffebee',
                    borderRadius: '6px',
                    border: '1px solid #ffcdd2',
                    textAlign: 'center',
                    animation: 'shake 0.3s'
                }}>
                    ⚠️ {validationError}
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {onBack && question.sortOrder > 1 && (
                    <button className="ae-btn secondary" style={{ flex: 1 }} onClick={onBack}>
                        이전
                    </button>
                )}
                <button className="next-btn" style={{ flex: 2, margin: 0 }} onClick={handleSubmit}>
                    다음
                </button>
            </div>
        </div>
    );
};

export default QuestionRenderer;
